/**
 * Toast-style error notification component
 *
 * This component provides a toast-style notification for errors
 * that can be dismissed or retried.
 */

import { useEffect, useState } from "react";
import type { BaseError } from "@charphq/errors";
import { getUserFriendlyMessage, shouldShowRetryButton } from "@charphq/errors";
import { Toast, ToastAction, ToastDescription, ToastTitle } from "../base/toast";

/**
 * Props for error toast component
 */
export interface ErrorToastProps {
  error: BaseError | null;
  onRetry?: () => void;
  onDismiss?: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
  className?: string;
}

/**
 * Toast-style error notification
 */
export function ErrorToast({
  error,
  onRetry,
  onDismiss,
  autoHide = true,
  autoHideDelay = 5000,
  className = "",
}: ErrorToastProps): React.JSX.Element | null {
  const [isVisible, setIsVisible] = useState(!!error);

  useEffect(() => {
    if (error) {
      setIsVisible(true);

      if (autoHide) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          onDismiss?.();
        }, autoHideDelay);

        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }

    return undefined;
  }, [error, autoHide, autoHideDelay, onDismiss]);

  if (!error || !isVisible) {
    return null;
  }

  const message = getUserFriendlyMessage(error);
  const canRetry = shouldShowRetryButton(error);

  return (
    <Toast className={className}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <div className="w-5 h-5">⚠️</div>
        </div>

        <div className="ml-3 flex-1">
          <ToastTitle>Error ({error.code})</ToastTitle>
          <ToastDescription>{message}</ToastDescription>

          <div className="mt-2 flex space-x-2">
            {canRetry && onRetry && (
              <ToastAction onClick={onRetry} altText="Retry">
                Retry
              </ToastAction>
            )}

            {onDismiss && (
              <ToastAction
                onClick={() => {
                  setIsVisible(false);
                  onDismiss();
                }}
                altText="Dismiss"
              >
                Dismiss
              </ToastAction>
            )}
          </div>
        </div>
      </div>
    </Toast>
  );
}
